import * as React from 'react';
import { DATA_ATTRIBUTES, DATA_HOOKS } from './constants';
import { ChevronDown } from '@wix/wix-ui-icons-common/on-stage';
import {
  TPAComponentsConfig,
  TPAComponentsContext,
} from '../TPAComponentsConfig';
import { classes, st } from './DropdownNativeSelect.st.css';
import { DropdownNativeSelectProps } from './types';
import { Spinner, SpinnerTypes } from '../Spinner';

class DropdownNativeSelectComponent extends React.Component<DropdownNativeSelectProps> {
  static contextType = TPAComponentsContext;

  private onSelect(e) {
    const { selectedOption: prevSelectedOption } = this.props;
    const optionId = e.target.value;
    if ((prevSelectedOption && prevSelectedOption.id) === optionId) {
      return;
    }
    const selectedOption = this.props.options.find(({ id }) => id === optionId);
    this.props.onSelect(selectedOption);
  }

  private renderOptions(hasPlaceholder: boolean) {
    const { placeholder, options } = this.props;
    return (
      <>
        {hasPlaceholder && (
          <option
            data-hook={DATA_HOOKS.placeholderOption}
            value=""
            disabled
            hidden={!options.length}
          >
            {placeholder}
          </option>
        )}
        {options.map((option, i) =>
          !option.divider ? (
            <option key={i} value={option.id} disabled={!option.isSelectable}>
              {option.value}
            </option>
          ) : null,
        )}
      </>
    );
  }

  private renderOptionIcon() {
    return (
      <div className={classes.optionIcon}>{this.props.selectedOption.icon}</div>
    );
  }

  private getDataAttributes() {
    const { error } = this.props;
    return {
      [DATA_ATTRIBUTES.error]: error,
    };
  }

  render() {
    const {
      disabled,
      readOnly,
      selectedOption,
      error,
      errorMessage,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      'aria-required': ariaRequired,
      id: dropdownId,
      className,
      name,
      errorComponent,
      newErrorMessage,
      isLoading,
      'loading-aria-label': loadingAriaLabel,
    } = this.props;

    const { rtl } = this.context as TPAComponentsConfig;

    const hasPlaceholder = !selectedOption || !selectedOption.value;
    const shouldRenderIcon = selectedOption && !!selectedOption.icon;

    const nativeSelectStyles = st(
      classes.root,
      {
        error,
        disabled,
        readOnly,
        placeholder: hasPlaceholder,
        icon: shouldRenderIcon,
        rtl,
        fullWidth: true,
      },
      className,
    );
    return (
      <div
        className={classes.wrapper}
        onClick={(e) => readOnly && e.preventDefault()}
        onMouseDown={(e) => readOnly && e.preventDefault()}
        onKeyDown={(e) => readOnly && e.preventDefault()}
      >
        <select
          {...this.getDataAttributes()}
          defaultValue=""
          {...(selectedOption && { value: selectedOption.id })}
          id={dropdownId}
          onChange={(e) => !readOnly && this.onSelect(e)}
          data-hook={DATA_HOOKS.nativeSelect}
          ref={this.props.innerRef}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          aria-required={ariaRequired}
          disabled={disabled}
          name={name}
          aria-readonly={readOnly}
          className={nativeSelectStyles}
        >
          {this.renderOptions(hasPlaceholder)}
        </select>
        {shouldRenderIcon ? this.renderOptionIcon() : null}
        {error && errorMessage && !newErrorMessage && errorComponent}
        <div className={classes.suffixContainer}>
          {isLoading ? (
            <Spinner
              className={classes.loader}
              data-hook={DATA_HOOKS.loader}
              type={SpinnerTypes.slim}
              aria-label={loadingAriaLabel}
            />
          ) : (
            <ChevronDown className={classes.arrowIcon} />
          )}
        </div>
      </div>
    );
  }
}

export const DropdownNativeSelect = React.forwardRef<
  HTMLSelectElement,
  DropdownNativeSelectProps
>((props, ref: React.RefObject<HTMLSelectElement>) => (
  <DropdownNativeSelectComponent {...props} innerRef={ref} />
));
